<template>
  <div class="addenda-inline">
    <div v-if="loading" class="addenda-inline-loading text-muted">
      <i class="ri-loader-4-line addenda-spin mr-1"></i>Cargando adendas…
    </div>

    <div v-else-if="!addenda.length" class="text-muted addenda-inline-empty">
      No hay adendas para mostrar.
    </div>

    <table v-else class="addenda-inline-table w-full">
      <thead>
        <tr>
          <th>Fecha efectiva</th>
          <th>Estado</th>
          <th>Campos modificados</th>
          <th>Motivo</th>
          <th>Documento</th>
          <th class="text-center">Acción</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="a in addenda" :key="a.id">
          <td>{{ formatDate(a.effectiveDate) }}</td>
          <td>
            <wz-badge :variant="statusVariant(a.status)" pill>{{ statusLabel(a.status) }}</wz-badge>
          </td>
          <td>
            <template v-if="a.changedFields && a.changedFields.length">
              <wz-badge
                v-for="f in a.changedFields"
                :key="f"
                variant="info-solid"
                pill
                class="mr-1 mb-1"
              >{{ fieldLabel(f) }}</wz-badge>
            </template>
            <span v-else class="text-muted">Solo documento</span>
          </td>
          <td>{{ a.reason || '—' }}</td>
          <td>
            <a
              href="#"
              class="addenda-doc-link"
              :class="{ 'is-disabled': downloadingId === a.id }"
              @click.prevent="downloadingId === a.id ? null : download(a)"
            >
              <i class="ri-file-pdf-2-line mr-1"></i>{{ a.fileName || 'adenda.pdf' }}
            </a>
          </td>
          <td class="text-center">
            <wz-button variant="outline" size="sm" @click="view(a)">
              <i class="ri-eye-line mr-1"></i>Ver
            </wz-button>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import { Alert } from '@wizepe/wizeui'
import ContractAddendumService from '@/core/services/ContractAddendumService'
import { downloadErrorMessage } from '@/core/utils'
import fileDownload from 'js-file-download'

// Etiquetas de campos de adenda (mismas claves que ContractAddendaSection.fieldOptions).
const FIELD_LABELS = {
  BASE_SALARY: 'Sueldo',
  POSITION: 'Cargo',
  AREA: 'Área',
  CHANNEL: 'Canal',
  EXPENSE_LINE: 'Línea de gasto',
  WORK_SITE: 'Sede',
  DIRECT_MANAGER: 'Jefe directo',
  HR_RESPONSIBLE_EMAIL: 'Responsable',
}

export default {
  name: 'AddendaInlineTable',
  props: {
    contractId: { type: [Number, String], required: true },
    addenda: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
  },
  data() {
    return {
      downloadingId: null,
    }
  },
  methods: {
    fieldLabel(key) {
      return FIELD_LABELS[key] || key
    },
    statusLabel(status) {
      if (status === 'APLICADA') return 'Aplicada'
      if (status === 'PENDIENTE') return 'Pendiente'
      return status || '—'
    },
    statusVariant(status) {
      if (status === 'APLICADA') return 'success'
      if (status === 'PENDIENTE') return 'warning-solid'
      return 'secondary'
    },
    // Fecha (YYYY-MM-DD) formateada por componentes para evitar el corrimiento de zona horaria.
    formatDate(value) {
      if (!value) return '—'
      const parts = String(value).slice(0, 10).split('-')
      if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`
      const d = new Date(value)
      return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('es-PE')
    },
    async download(a) {
      try {
        this.downloadingId = a.id
        const response = await ContractAddendumService.download(this.contractId, a.id)
        fileDownload(response.data, a.fileName || 'adenda.pdf')
      } catch (e) {
        Alert.error(await downloadErrorMessage(e) || 'Error al descargar la adenda')
        console.error(e)
      } finally {
        this.downloadingId = null
      }
    },
    // Navega al detalle del contrato posicionado en esta adenda (scroll + highlight vía query addendaId).
    view(a) {
      this.$router.push({
        name: 'contracts-detail',
        params: { id: this.contractId },
        query: { addendaId: a.id },
      })
    },
  },
}
</script>

<style lang="stylus" scoped>
.addenda-inline
  padding 10px 14px

.addenda-inline-loading,
.addenda-inline-empty
  font-size 13px
  padding 4px 0

.addenda-spin
  display inline-block
  animation addenda-rotate 0.8s linear infinite

@keyframes addenda-rotate
  from
    transform rotate(0deg)
  to
    transform rotate(360deg)

.addenda-inline-table
  border-collapse collapse
  font-size 13px

  th, td
    border 1px solid var(--admin-border-color)
    padding 6px 10px
    text-align left
    vertical-align middle

  th
    background var(--admin-bg-muted, #f8f9fa)
    font-weight 600
    white-space nowrap

.addenda-doc-link
  color var(--admin-primary, #2563eb)
  text-decoration none
  word-break break-word

  &:hover
    text-decoration underline

  &.is-disabled
    opacity 0.5
    pointer-events none
    cursor default
</style>
